import constants from '#packages/constants';
import { hoc } from '#packages/util';
import _ from 'lodash';
import React from 'react';
import { UNSCALED_DROPZONE_HEIGHT } from './constants';
import { SectionsDropZone } from './SectionsDropZone';
import styles from './SectionsDropZoneContainer.scss';
import {
  mapDispatchToProps,
  mapStateToProps,
  type SectionsDropZoneContainerDispatchProps,
  type SectionsDropZoneContainerStateProps,
} from './SectionsDropZoneContainerMapper';
import { panels } from '#packages/stateManagement';
import type { SectionWithLayout } from '#packages/sections';

interface SectionsDropZoneContainerOwnProps {}

interface SectionsDropZoneContainerProps
  extends SectionsDropZoneContainerOwnProps,
    SectionsDropZoneContainerStateProps,
    SectionsDropZoneContainerDispatchProps {}

interface SectionsDropZoneContainerState {
  dropzoneIndex: number;
  dragOverContainer: boolean;
}

const OFF_SCREEN_Y = -1000;
const getSectionWithLayoutHash = ({ ref, layout }: SectionWithLayout) =>
  `${ref.id}-${layout.y}`;

export class SectionsDropZoneContainerComponent extends React.Component<
  SectionsDropZoneContainerProps,
  SectionsDropZoneContainerState
> {
  constructor(props: SectionsDropZoneContainerProps) {
    super(props);

    const { stageEntryIndex } = props;

    const dropzoneIndex = stageEntryIndex ?? this.calcDropzoneIndex();

    this.state = {
      dropzoneIndex,
      dragOverContainer: false,
    };
  }

  componentDidMount() {
    const { dropzoneIndex } = this.state;
    const {
      setAutosaveEnabled,
      setSectionsStageEntryIndex,
      setDropZoneExtraSiteHeight,
    } = this.props;

    setAutosaveEnabled(false);
    setDropZoneExtraSiteHeight();
    this.setTransformations({ shouldClear: false, dropzoneIndex });
    setSectionsStageEntryIndex(dropzoneIndex);
  }

  componentDidUpdate(prevProps: SectionsDropZoneContainerProps) {
    const {
      scrollTop,
      isDragOverStageInProgress,
      shouldDisplayStaticDropZone,
      sectionsWithLayout,
    } = this.props;

    if (isDragOverStageInProgress || this.state.dragOverContainer) {
      if (shouldDisplayStaticDropZone) {
        return;
      }

      const nextDropzoneIndex = this.calcDropzoneIndexByMousePosition(
        this.props.mousePosition.y,
      );
      this.setDropzoneIndexIfNeeded(nextDropzoneIndex);
      return;
    }

    const areSectionsEqual = _.isEqual(
      sectionsWithLayout.map(getSectionWithLayoutHash),
      prevProps.sectionsWithLayout.map(getSectionWithLayoutHash),
    );

    if (!areSectionsEqual) {
      this.setDropzoneIndex(this.calcDropzoneIndex());
      return;
    }
    const isScrollChanged = scrollTop !== prevProps.scrollTop;
    if (isScrollChanged) {
      this.setDropzoneIndexIfNeeded(this.calcDropzoneIndex());
      return;
    }
  }

  componentWillUnmount() {
    this.clearTransformations();
    this.props.setAutosaveEnabled(true);
  }

  setDropzoneIndexIfNeeded(
    dropzoneNextIndex: number,
    disableStaticDropZone?: boolean,
  ) {
    if (dropzoneNextIndex !== this.state.dropzoneIndex) {
      this.setDropzoneIndex(dropzoneNextIndex, disableStaticDropZone);
    }
  }

  setTransformations({
    shouldClear,
    dropzoneIndex,
    disableStaticDropZone,
  }: {
    shouldClear: boolean;
    dropzoneIndex: number;
    disableStaticDropZone?: boolean;
  }) {
    const {
      sectionsWithLayout,
      updateComponentTransformation,
      removeComponentTransformation,
      siteFooterRef,
      shouldDisplayStaticDropZone,
      siteScale,
    } = this.props;

    if (shouldDisplayStaticDropZone && !disableStaticDropZone && !shouldClear) {
      const hasSpaceForDropzoneText =
        sectionsWithLayout[dropzoneIndex]?.layout.height >=
        UNSCALED_DROPZONE_HEIGHT;

      if (!hasSpaceForDropzoneText) {
        /* Increase dropzone height to give a space for a dropzone placeholder text */
        const unscaledSectionHeight =
          sectionsWithLayout[dropzoneIndex]?.layout.height / siteScale;
        const diff = UNSCALED_DROPZONE_HEIGHT - unscaledSectionHeight;
        updateComponentTransformation(siteFooterRef, {
          translate: {
            y: { value: diff, type: 'px' },
          },
        });
      } else {
        this.clearTransformations();
      }

      return;
    }

    [...sectionsWithLayout, { ref: siteFooterRef }].forEach((s, index) => {
      const translateYValue =
        dropzoneIndex <= index && !shouldClear ? UNSCALED_DROPZONE_HEIGHT : 0;
      if (translateYValue) {
        updateComponentTransformation(s.ref, {
          translate: { y: { value: translateYValue, type: 'px' } },
        });
      } else {
        removeComponentTransformation(s.ref);
      }
    });
  }

  clearTransformations() {
    this.setTransformations({ shouldClear: true, dropzoneIndex: Infinity });
  }

  setDropzoneIndex(dropzoneIndex: number, disableStaticDropZone?: boolean) {
    this.setTransformations({
      shouldClear: false,
      dropzoneIndex,
      disableStaticDropZone,
    });
    this.setState({ dropzoneIndex });
    this.props.setSectionsStageEntryIndex(dropzoneIndex);
  }

  calcDropzoneOffsetTop() {
    const { sectionsWithLayout } = this.props;
    const { dropzoneIndex } = this.state;

    if (dropzoneIndex === 0) {
      return sectionsWithLayout[0]?.layout?.y ?? OFF_SCREEN_Y;
    }

    const layout = sectionsWithLayout[dropzoneIndex - 1]?.layout;
    return layout ? layout.y + layout.height : OFF_SCREEN_Y;
  }

  calcDropzoneIndex() {
    const {
      sectionsWithLayout,
      scrollTop,
      previewHeight,
      emptyStateSectionReplacementRef,
    } = this.props;

    if (!sectionsWithLayout.length) return -1;

    if (sectionsWithLayout.length === 1) {
      return 0;
    }

    if (emptyStateSectionReplacementRef) {
      return sectionsWithLayout.findIndex(
        ({ ref }) => ref.id === emptyStateSectionReplacementRef.id,
      );
    }

    const absDistances = sectionsWithLayout.map(({ layout }) =>
      Math.abs(layout.y - scrollTop - previewHeight / 2),
    );
    const minDist = Math.min(...absDistances);

    return absDistances.indexOf(minDist);
  }

  findSectionIndex(clientY: number) {
    const { sectionsWithLayout } = this.props;

    const index = sectionsWithLayout.findIndex(({ layout }) => {
      const top = layout.y;
      const bottom = top + layout.height;

      return top <= clientY && bottom >= clientY;
    });

    return index > -1 ? index : sectionsWithLayout.length - 1;
  }

  calcDropzoneIndexByMousePosition(clientY: number) {
    const { sectionsWithLayout } = this.props;
    const sectionIndex = this.findSectionIndex(clientY);
    const sectionLayout = sectionsWithLayout[sectionIndex].layout;
    const isAboveSection = clientY < sectionLayout.y + sectionLayout.height / 2;
    return isAboveSection ? sectionIndex : sectionIndex + 1;
  }

  render() {
    const { shouldDisplayStaticDropZone, sectionsWithLayout, siteScale } =
      this.props;
    const { dropzoneIndex } = this.state;

    const dropZoneStyle = {
      top: this.calcDropzoneOffsetTop(),
      height: shouldDisplayStaticDropZone
        ? Math.max(
            sectionsWithLayout[dropzoneIndex]?.layout.height,
            UNSCALED_DROPZONE_HEIGHT * siteScale,
          )
        : UNSCALED_DROPZONE_HEIGHT * siteScale,
    };

    return (
      <div
        className={styles.container}
        onMouseEnter={() => this.setState({ dragOverContainer: true })}
        onMouseLeave={() => this.setState({ dragOverContainer: false })}
      >
        <SectionsDropZone
          isDragging={this.props.isSectionDragToStageInProgress}
          style={dropZoneStyle}
          shouldDisplayStaticDropZone={shouldDisplayStaticDropZone}
        />
      </div>
    );
  }
}

const ConnectedSectionsDropZoneContainer = hoc.connect(
  hoc.STORES.MOUSE_OPS,
  mapStateToProps,
  mapDispatchToProps,
)(SectionsDropZoneContainerComponent);

export const SectionsDropZoneContainer = hoc.withConditionalRender(
  hoc.STORES.EDITOR_API,
  ({ state, editorAPI }) => {
    const isAddSectionPanelOpened = panels.selectors.isPanelOpenedByName(
      state,
      constants.ROOT_COMPS.LEFTBAR.ADD_SECTION_PANEL_NAME,
    );

    return (
      isAddSectionPanelOpened &&
      editorAPI.zoomMode.isStageZoomMode() &&
      !editorAPI.zoomMode.isZoomModeTransitionActive()
    );
  },
)(ConnectedSectionsDropZoneContainer);
